import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../../styles/pages.css";
import Banner from "../ui/Banner";
import Boardimg from "../../assets/images/board.png";
import Pagination from "../ui/Pagination";
import EnSearchBar from "../ui/EnSearchBar";
import { getListBoardNotice, postContact } from "../../api";

const EnBoard = () => {
  const navigate = useNavigate();
  const { tab } = useParams();
  const activeTab = tab || "contact";

  const handleTabChange = (tabId) => {
    navigate(`/en/board/${tabId}`);
  };

  const bannerConfig = {
    backgroundImage: Boardimg,
    title: "Q&A",
    description: ["Just check it out once before you ask!", "I only gathered key information to quickly solve my curiosity."],
    tabs: [
      { id: "contact", name: "Quotation inquiry" },
      { id: "notice", name: "Notice" },
    ],
  };

  return (
    <div className="board-container">
      <Banner {...bannerConfig} activeTab={activeTab} onTabChange={handleTabChange} />
      {activeTab === "contact" && <ContactContent />}
      {activeTab === "notice" && <NoticeContent />}
    </div>
  );
};

const ContactContent = () => {
  const MAX_FILE_SIZE = 20 * 1024 * 1024;
  const ALLOWED_EXTS = ["jpg", "jpeg", "png", "pdf", "docx", "xls", "xlsx", "ppt", "pptx", "hwp"];

  const [formData, setFormData] = useState({
    company: "",
    name: "",
    phone: "",
    email: "",
    emailDomain: "",
    category: "작물파쇄",
    title: "",
    content: "",
    agreeToTerms: false,
  });

  const [customDomain, setCustomDomain] = useState("");
  const [isCustomDomain, setIsCustomDomain] = useState(true);
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleCustomDomainChange = (e) => {
    setCustomDomain(e.target.value);
    setFormData({
      ...formData,
      emailDomain: e.target.value,
    });
  };

  const handleEmailDomainChange = (e) => {
    const selectedDomain = e.target.value;
    setFormData({
      ...formData,
      emailDomain: selectedDomain,
    });

    if (selectedDomain === "") {
      setIsCustomDomain(true);
      setCustomDomain("");
    } else {
      setIsCustomDomain(false);
      setCustomDomain(selectedDomain);
    }
  };

  const handleCategoryClick = (category) => {
    setFormData({
      ...formData,
      category,
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return setFile(null);
    if (selectedFile.size > MAX_FILE_SIZE) {
      alert("The file size exceeds 20MB.");
      e.target.value = "";
      return;
    }
    const ext = (selectedFile.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTS.includes(ext)) {
      alert("This is not an attachable file extension(type).");
      e.target.value = "";
      return;
    }
    setFile(selectedFile);
  };

  const handleCheckboxChange = (e) => {
    setFormData({
      ...formData,
      agreeToTerms: e.target.checked,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const domain = isCustomDomain ? customDomain : formData.emailDomain;
    const finalEmail = `${formData.email}@${domain}`.trim();

    const data = {
      company: formData.company,
      name: formData.name,
      phone: formData.phone.replace(/\s|-/g, ""),
      email: finalEmail,
      inquiry: formData.category,
      title: formData.title,
      content: formData.content,
      file: formData.file,
    };

    setSubmitting(true);
    try {
      let response = await postContact(data);

      if ([200, 201, 202].includes(response.status)) {
        alert("Your inquiry has been received.");
        window.scrollTo(0, 0);
        setTimeout(() => {
          window.location.reload();
        }, 10);
      } else {
        alert("Your request has been sent, but your response is not what you expected. Please try again in a moment.");
      }
    } catch (err) {
      const status = err?.response?.status || 0;
      const data = err?.response?.data;
      if (status >= 500) {
        alert("Server error. Please try again in a moment.");
      } else if (data?.detail) {
        alert(data.detail);
      } else if (typeof data === "string") {
        alert(data);
      } else if (data && typeof data === "object") {
        const msgs = [];
        Object.entries(data).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach((m) => msgs.push(`${k}: ${m}`));
          else if (typeof v === "string") msgs.push(`${k}: ${v}`);
        });
        alert(msgs.join("\n") || "Your request could not be processed.");
      } else {
        alert("Network error has occurred.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="contact-section">
      <div className="contact-header">
        <h2>Contact us</h2>
        <p>Experts will guide you through optimized greenhouse solutions. </p>
        <p>Carefully compare performance, price, and construction schedule.</p>
      </div>

      <form onSubmit={handleSubmit} className="contact-form">
        <div className="form-row">
          <div className="form-label">
            <label htmlFor="company">
              Company name <span className="required">*</span>
            </label>
          </div>
          <div className="form-input">
            <input type="text" id="company" name="company" value={formData.company} onChange={handleInputChange} placeholder="Please enter your company name." required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-label">
            <label htmlFor="name">
              Name <span className="required">*</span>
            </label>
          </div>
          <div className="form-input">
            <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} placeholder="Please enter your name." required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-label">
            <label htmlFor="phone">
              Phone number <span className="required">*</span>
            </label>
          </div>
          <div className="form-input">
            <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="Please enter your phone number." required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-label">
            <label htmlFor="email">
              Email address <span className="required">*</span>
            </label>
          </div>
          <div className="form-input email-group">
            <div className="email-input-container">
              <input type="text" id="email" name="email" className="email-field" value={formData.email} onChange={handleInputChange} placeholder="Please enter your email." required />
              <span className="email-at">@</span>
              <input type="text" className="email-field" value={customDomain} onChange={handleCustomDomainChange} placeholder="Enter directly" disabled={formData.emailDomain !== ""} />
              <div className="email-domain-select-wrapper">
                <select value={formData.emailDomain} onChange={handleEmailDomainChange} className="email-field email-domain-select">
                  <option value="">Enter directly</option>
                  <option value="gmail.com">gmail.com</option>
                  <option value="naver.com">naver.com</option>
                  <option value="daum.net">daum.net</option>
                  <option value="hanmail.net">hanmail.net</option>
                  <option value="nate.com">nate.com</option>
                </select>
                <div className="select-arrow">
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="8" viewBox="0 0 14 8">
                    <path d="M1 1L7 7L13 1" stroke="#888" strokeWidth="2" fill="none" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="form-row formcontact">
          <div className="form-label">
            <label>
              Inquiry classification <span className="required">*</span>
            </label>
          </div>
          <div className="form-input">
            <div className="category-buttons">
              <button type="button" className={`category-button ${formData.category === "작물파쇄" ? "active" : ""}`} onClick={() => handleCategoryClick("작물파쇄")}>
                crop crushing
              </button>
              <button type="button" className={`category-button ${formData.category === "소모자재" ? "active" : ""}`} onClick={() => handleCategoryClick("소모자재")}>
                consumable materials
              </button>
              <button type="button" className={`category-button ${formData.category === "차광/세척제" ? "active" : ""}`} onClick={() => handleCategoryClick("차광/세척제")}>
                a shading/cleaning agent
              </button>
              <button type="button" className={`category-button ${formData.category === "소독제" ? "active" : ""}`} onClick={() => handleCategoryClick("소독제")}>
                disinfectant
              </button>
              <button type="button" className={`category-button ${formData.category === "온실개폐장치" ? "active" : ""}`} onClick={() => handleCategoryClick("온실개폐장치")}>
                Greenhouse opening/closing device
              </button>
              <button type="button" className={`category-button ${formData.category === "기계장비" ? "active" : ""}`} onClick={() => handleCategoryClick("기계장비")}>
                mechanical equipment
              </button>
              <button type="button" className={`category-button ${formData.category === "시공" ? "active" : ""}`} onClick={() => handleCategoryClick("시공")}>
                Construction
              </button>
              <button type="button" className={`category-button ${formData.category === "유지보수" ? "active" : ""}`} onClick={() => handleCategoryClick("유지보수")}>
                Maintenance
              </button>
              <button type="button" className={`category-button ${formData.category === "기타" ? "active" : ""}`} onClick={() => handleCategoryClick("기타")}>
                etc.
              </button>
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-label">
            <label htmlFor="title">
              Title <span className="required">*</span>
            </label>
          </div>
          <div className="form-input">
            <input type="text" id="title" name="title" value={formData.title} onChange={handleInputChange} placeholder="Please enter up to 16 characters." maxLength="16" required />
          </div>
        </div>

        <div className="form-row">
          <div className="form-label">
            <label htmlFor="content">
              Content <span className="required">*</span>
            </label>
          </div>
          <div className="form-input">
            <textarea
              id="content"
              name="content"
              value={formData.content}
              onChange={handleInputChange}
              placeholder="Please enter no more than 500 characters."
              maxLength="500"
              rows="6"
              required
            ></textarea>
          </div>
        </div>

        <div className="form-row">
          <div className="form-label">
            <label htmlFor="file">The attached file</label>
          </div>
          <div className="form-input">
            <div className="file-upload-container">
              <input type="text" className="file-display" placeholder="attached file" value={file ? file.name : ""} readOnly />
              <input type="file" id="file" onChange={handleFileChange} className="file-input" />
              <button type="button" className="file-upload-button" onClick={() => document.getElementById("file").click()}>
                Find a file
              </button>
            </div>
            <div className="file-info">
              <p>• The attachment capacity cannot exceed maximum 20MB.</p>
              <p>• The registered extension is jpg, jpg, pg, pdf, xls, xls.</p>
              <p>• The attached file tidal flats are one.</p>
            </div>
          </div>
        </div>

        <div className="privacy-agreement">
          <h3>Collection and Use of Personal Information</h3>
          <div className="privacy-content">
            <p>
              ECOTECH GLOBAL Co., Ltd. complies with the relevant regulations of the Personal Information Protection Act and other applicable laws to ensure that the personal information you provide
              is collected, retained, and processed safely.
            </p>
            <p>Please review the following information carefully and decide whether to consent to the collection, use, and third-party provision of your personal information.</p>

            <ol>
              <li>
                Items of Personal Information Collected
                <p>(1) Required: Name, Contact Number, Email Address, Inquiry Details (Subject and Message), Company Name, Job Title</p>
                <p>(2) Optional: Business Information, Attached Files</p>
              </li>

              <li>
                Purpose of Use
                <p>The collected personal information will be used for the following purpose:</p>
                <p>(1) Providing responses to your inquiries</p>
              </li>

              <li>
                Retention and Use Period
                <p>Your personal information will be retained and used for the period agreed upon below and will be destroyed without delay once the retention period expires.</p>
                <p>(1) Personal information collected through email inquiries or consultations: 3 years</p>
              </li>

              <li>
                Right to Refuse Consent and Possible Disadvantages
                <p>You may refuse to consent to the collection and use of your personal information. However, refusal may result in limitations when using the consultation service.</p>
              </li>
            </ol>
          </div>
        </div>
        <div className="agreement-checkbox">
          <input type="checkbox" id="agreeToTerms" checked={formData.agreeToTerms} onChange={handleCheckboxChange} required />
          <label htmlFor="agreeToTerms">(Required) I agree to the terms and conditions for collecting and using personal information.</label>
        </div>
        <div className="submit-button-container">
          <button type="submit" className="submit-button" disabled={!formData.agreeToTerms}>
            Send
          </button>
        </div>
      </form>
    </div>
  );
};

const NoticeContent = () => {
  const navigate = useNavigate();

  // Pagination 시작
  const [currentPage, setCurrentPage] = useState(1);
  const [displayedNotices, setDisplayedNotices] = useState([]);
  const noticesPerPage = 15;
  const [dataCount, setDataCount] = useState(1);
  // Pagination 끝

  // 검색바
  // const [originalNotices, setOriginalNotices] = useState([]);
  // const [filteredNotices, setFilteredNotices] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [hoveredItemId, setHoveredItemId] = useState(null);
  // 언어 설정
  const currentLang = "en";

  // 초기 로드 시 모든 공지사항 설정
  useEffect(() => {
    (async () => {
      const res = await getListBoardNotice({
        category: "공지",
        page: currentPage,
        search: searchTerm.trim() || undefined,
        lang: currentLang,
      });
      const data = res.data.results;
      setDataCount(res.data.count);
      setDisplayedNotices(data);
    })();
  }, [currentPage, searchTerm]);

  // 검색어에 따라 공지사항 필터링
  useEffect(() => {
    // if (searchTerm.trim() === "") {
    //   setFilteredNotices(originalNotices);
    // } else {
    //   const filtered = originalNotices.filter(
    //     (notice) =>
    //       notice.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    //       notice.notice_content.toLowerCase().includes(searchTerm.toLowerCase())
    //   );
    //   setFilteredNotices(filtered);
    // }
    setCurrentPage(1); // 검색 시 첫 페이지로 이동
  }, [searchTerm]);

  // 페이지 변경 시 표시할 공지사항 업데이트
  // useEffect(() => {
  //   const indexOfLastNotice = currentPage * noticesPerPage;
  //   const indexOfFirstNotice = indexOfLastNotice - noticesPerPage;
  //   setDisplayedNotices(filteredNotices.slice(indexOfFirstNotice, indexOfLastNotice));
  // }, [currentPage, filteredNotices]);

  const handleNoticeClick = (id) => {
    navigate(`/en/board/notice/${id}/`);
  };

  const handlePageChange = (pageNumber) => {
    const target = Math.max(1, Math.min(pageNumber, totalPages));
    setCurrentPage((prev) => (prev === target ? prev : target));
    window.scrollTo({
      top: document.querySelector(".notice-section").offsetTop - 100,
      behavior: "smooth",
    });
  };

  const handleSearch = (term) => {
    setCurrentPage(1);
    setSearchTerm(term);
  };

  const totalPages = Math.ceil((dataCount ?? 0) / noticesPerPage);

  return (
    <div className="notice-section">
      <div className="productarea-header">
        <h2>Notice</h2>
        <p>Check out ECOTECH GLOBAL's new news and guidance.</p>
      </div>
      <div className="productarea-info">
        <p>{dataCount} items</p>
        <EnSearchBar onSearch={handleSearch} />
      </div>
      <hr />
      <div className="notice-list">
        {displayedNotices.map((notice) => (
          <div key={notice.id} className="notice-item" onClick={() => handleNoticeClick(notice.id)} onMouseEnter={() => setHoveredItemId(notice.id)} onMouseLeave={() => setHoveredItemId(null)}>
            <div className="notice-info">
              <p className="date">{notice.created_date}</p>
              <p className="year">{notice.created_year}</p>
            </div>
            <div className="notice-title">
              <p className="title">
                {notice.top_fixed && (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: "8px", verticalAlign: "middle" }}>
                    <path
                      d="M16 3H8C7.44772 3 7 3.44772 7 4V7C7 7.55228 7.44772 8 8 8H8.5V14.5L6 18H18L15.5 14.5V8H16C16.5523 8 17 7.55228 17 7V4C17 3.44772 16.5523 3 16 3Z"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path d="M12 18V21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                )}
                {notice.en_translations[0].title_en}
              </p>
              <p className="description">{notice.en_translations[0].notice_content_text_en}</p>
            </div>
            <div className="notice-detail">
              <svg xmlns="http://www.w3.org/2000/svg" width="54" height="54" viewBox="0 0 54 54" className="arrow-circle">
                <g data-name="그룹 66">
                  <g data-name="타원 2">
                    <circle
                      cx="27"
                      cy="27"
                      r="27"
                      style={{
                        stroke: "none",
                        fill: hoveredItemId === notice.id ? "#3076bc" : "#fff",
                        transition: "fill 0.3s ease",
                      }}
                    />
                    <circle
                      cx="27"
                      cy="27"
                      r="26.5"
                      style={{
                        fill: "none",
                        stroke: hoveredItemId === notice.id ? "#3076bc" : "#676767",
                        transition: "stroke 0.3s ease",
                      }}
                    />
                  </g>
                  <g data-name="그룹 64">
                    <path
                      data-name="패스 46"
                      d="M19177 1666.584h14.877"
                      transform="translate(-19157 -1639.662)"
                      style={{
                        strokeLinecap: "round",
                        fill: "none",
                        stroke: hoveredItemId === notice.id ? "#fff" : "#676767",
                        transition: "stroke 0.3s ease",
                      }}
                    />
                    <path
                      data-name="패스 47"
                      d="m19424.426 1615.443 6.922 6.922-6.922 6.922"
                      transform="translate(-19396.471 -1595.443)"
                      style={{
                        strokeLinecap: "round",
                        fill: "none",
                        stroke: hoveredItemId === notice.id ? "#fff" : "#676767",
                        transition: "stroke 0.3s ease",
                      }}
                    />
                  </g>
                </g>
              </svg>
            </div>
          </div>
        ))}
      </div>

      {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />}
    </div>
  );
};

export default EnBoard;
